import React, { useEffect, useRef } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { OHIF_COLORS } from 'app/features/reports/view';

export type ViewerTab = 'images' | 'reports';

// Outline glyphs on a 24px stroke canvas (Instagram-style tab icons), thicker
// when the tab is active. Same icons as the v3 viewer's tab bar.
const TabIcon = ({ tab, active }: { tab: ViewerTab; active: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    width={24}
    height={24}
    fill="none"
    stroke="currentColor"
    strokeWidth={active ? 2.25 : 1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {tab === 'images' ? (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2.5" />
        <circle cx="9" cy="9" r="1.75" />
        <path d="m21 15-4.5-4.5L6 21" />
      </>
    ) : (
      <>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
        <path d="M9 13h6M9 17h4" />
      </>
    )}
  </svg>
);

const TABS: Array<{ id: ViewerTab; label: string }> = [
  { id: 'images', label: 'Imágenes' },
  { id: 'reports', label: 'Informes' },
];

// The viewer's height (styles/ohif-viewer-overrides.css) leaves room for the bar.
const TABBAR_HEIGHT_VAR = '--evodicom-tabbar-height';

/**
 * Bottom navigation under the images viewer: the images or the study's
 * reports (the PDF report viewer over them). "Informes" is disabled while
 * the study has no reports.
 */
const ViewerTabBar = ({
  active,
  onChange,
  reportsEnabled,
}: {
  active: ViewerTab;
  onChange: (tab: ViewerTab) => void;
  reportsEnabled: boolean;
}) => {
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const root = document.documentElement;
    let frame = 0;
    // Next frame: the new height resizes the viewer, whose own resize
    // observers would otherwise run in the same loop ("ResizeObserver loop").
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const height = `${nav.getBoundingClientRect().height}px`;
        if (root.style.getPropertyValue(TABBAR_HEIGHT_VAR) !== height) {
          root.style.setProperty(TABBAR_HEIGHT_VAR, height);
        }
      });
    });
    observer.observe(nav);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      root.style.removeProperty(TABBAR_HEIGHT_VAR);
    };
  }, []);

  return (
    <Box
      component="nav"
      ref={navRef}
      aria-label="Secciones del estudio"
      sx={{
        display: 'flex',
        flexShrink: 0,
        bgcolor: OHIF_COLORS.background,
        borderTop: `1px solid ${OHIF_COLORS.input}`,
        pb: 'env(safe-area-inset-bottom)',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {TABS.map(({ id, label }) => {
        const isActive = active === id;
        return (
          <ButtonBase
            key={id}
            // Icon-only tabs: the label is kept for screen readers and as the hover hint.
            aria-label={label}
            title={label}
            aria-current={isActive ? 'page' : undefined}
            disabled={id === 'reports' && !reportsEnabled}
            onClick={() => onChange(id)}
            sx={{
              flex: 1,
              minHeight: 48,
              color: isActive ? OHIF_COLORS.primary : OHIF_COLORS.mutedForeground,
              '&:hover': { color: OHIF_COLORS.primary },
              '&.Mui-disabled': {
                color: OHIF_COLORS.input,
                pointerEvents: 'auto',
                cursor: 'not-allowed',
              },
            }}
          >
            <TabIcon tab={id} active={isActive} />
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default ViewerTabBar;
